// Admin user list. On the `components/ui` primitives: the create form is
// Card + Field/Input/Select, the status and plan filter chips are
// SegmentedControl, and the loading / empty / no-results blocks are Skeleton
// and EmptyState (which is why `pages/users/states.tsx` no longer exists).
//
// The list layer — useListState + SortableTh + Pagination — is untouched.
import { useState, useMemo } from 'react'
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  LinkButton,
  PageHeader,
  Pagination,
  SegmentedControl,
  Select,
  Skeleton,
  SortableTh,
  Spinner,
} from '../components/ui'
import { useListState } from '../hooks/useListState'
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { usersApi, type PlatformUser, type UserListParams } from '../api/client'
import { PLANS, capKey, extractApiError, supportedLanguages, type Plan, type SortKey } from './users/format'
import { UserRow } from './users/UserRow'
import { ResourceUsagePanel } from './users/resources'

export default function UsersPage() {
  const { t } = useTranslation()
  const qc = useQueryClient()

  const [showForm, setShowForm] = useState(false)
  const [email, setEmail] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [phone, setPhone] = useState('')
  const [limit, setLimit] = useState(3)
  const [plan, setPlan] = useState<Plan>('free')
  const [lang, setLang] = useState('en')
  const [formError, setFormError] = useState('')
  const [deleteError, setDeleteError] = useState('')

  const [expandedId, setExpandedId] = useState<string | null>(null)

  // Sort, debounced search, filters and paging all live in useListState — it
  // also puts us back on page 1 whenever any of them narrows the result set.
  const list = useListState<SortKey, { plan: Plan | 'all'; status: 'all' | 'active' | 'pending' }>({
    sortKey: 'created_at',
    defaultDirFor: (k) => (k === 'email' || k === 'status' || k === 'plan' ? 'asc' : 'desc'),
    filters: { plan: 'all', status: 'all' },
  })
  const { sortKey, sortDir, toggleSort, search, setSearch, debouncedSearch, page: currentPage, setPage: setCurrentPage, pageSize, setPageSize, filtersActive } = list
  const filterPlan = list.filters.plan
  const filterStatus = list.filters.status

  const queryParams: UserListParams = useMemo(() => ({
    page: currentPage,
    page_size: pageSize,
    q: debouncedSearch || undefined,
    status: filterStatus === 'all' ? undefined : filterStatus,
    plan: filterPlan === 'all' ? undefined : filterPlan,
    sort: sortKey,
    order: sortDir,
  }), [currentPage, pageSize, debouncedSearch, filterStatus, filterPlan, sortKey, sortDir])

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['users', 'paginated', queryParams],
    queryFn: () => usersApi.list(queryParams),
    placeholderData: keepPreviousData,
  })

  const users: PlatformUser[] = data?.items ?? []
  const total = data?.total ?? 0
  const serverPageSize = data?.page_size ?? pageSize

  const createMutation = useMutation({
    mutationFn: usersApi.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] })
      setShowForm(false)
      setEmail('')
      setCompanyName('')
      setPhone('')
      setLimit(3)
      setPlan('free')
      setLang('en')
      setFormError('')
    },
    onError: (e: unknown) => {
      setFormError(extractApiError(e) ?? t('users.createError'))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: usersApi.delete,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  })

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault()
    setFormError('')
    createMutation.mutate({ email, instance_limit: limit, plan, language: lang, company_name: companyName.trim(), phone: phone.trim() })
  }

  // mutate() + onError rather than a floating promise: a delete that 500s used
  // to leave the row in place with nothing said.
  const handleDelete = (id: string) => {
    setDeleteError('')
    deleteMutation.mutate(id, {
      onError: (e: unknown) => setDeleteError(extractApiError(e) ?? t('users.deleteError', { defaultValue: 'Failed to delete user' })),
    })
  }

  const { data: stats = [], isLoading: statsLoading, refetch: refetchStats } = useQuery({
    queryKey: ['user-stats'],
    queryFn: usersApi.stats,
    refetchInterval: 30000,
  })

  const { data: hostStats, isLoading: hostStatsLoading, refetch: refetchHostStats } = useQuery({
    queryKey: ['host-stats'],
    queryFn: usersApi.hostStats,
    refetchInterval: 30000,
  })

  const handleRefreshStats = () => {
    void refetchStats()
    void refetchHostStats()
  }

  const clearFilters = list.resetFilters

  const openForm = () => { setShowForm(true); setFormError('') }

  // BTK 5651: the monthly "Barındırılan Alan Adları" file, generated by the
  // backend from live instance/mail domains and each account's contact
  // identity. Downloaded here, uploaded by hand at internet.btk.gov.tr.
  const [btkDownloading, setBtkDownloading] = useState(false)
  const [btkError, setBtkError] = useState('')
  const handleBtkDownload = async () => {
    setBtkError('')
    setBtkDownloading(true)
    try {
      const blob = await usersApi.btkDomainsCsv()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `btk-barindirilan-alan-adlari-${new Date().toISOString().slice(0, 7)}.csv`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (e) {
      setBtkError(extractApiError(e) ?? t('users.btkExportError'))
    } finally {
      setBtkDownloading(false)
    }
  }

  return (
    <div className="p-4 sm:p-8 flex flex-col xl:flex-row gap-6 min-h-0">
      {/* Left column: user management */}
      <div className="flex-1 min-w-0">
        <PageHeader
          title={
            <span className="inline-flex items-center gap-2">
              {t('users.title')}
              {isFetching && !isLoading && (
                <span title={t('users.refreshing', { defaultValue: 'Refreshing…' })} className="text-gray-400">
                  <Spinner className="w-3.5 h-3.5" />
                </span>
              )}
            </span>
          }
          description={
            <>
              {t('users.subtitle')}
              {total > 0 && (
                <span className="block text-xs mt-1.5">{t('users.totalCount', { count: total })}</span>
              )}
            </>
          }
          actions={<Button onClick={openForm}>{t('users.add')}</Button>}
        />

        {/* Create form */}
        {showForm && (
          <Card className="mb-6">
            <form onSubmit={handleCreate}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-base font-semibold text-gray-900">{t('users.addTitle')}</h2>
                <LinkButton
                  tone="neutral"
                  onClick={() => setShowForm(false)}
                  aria-label={t('common.close')}
                  className="p-1 rounded-lg hover:bg-gray-100"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </LinkButton>
              </div>

              {formError && <Alert tone="danger" className="mb-4">{formError}</Alert>}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <Field label={t('users.email')} hint={t('users.inviteHint')} className="md:col-span-2">
                  {(p) => (
                    <Input
                      {...p}
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('users.emailPlaceholder')}
                    />
                  )}
                </Field>

                <Field label={t('users.companyName')} hint={t('users.companyNameHint')}>
                  {(p) => (
                    <Input
                      {...p}
                      type="text"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      placeholder={t('users.companyNamePlaceholder')}
                    />
                  )}
                </Field>

                <Field label={t('users.phone')}>
                  {(p) => (
                    <Input
                      {...p}
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder={t('users.phonePlaceholder')}
                    />
                  )}
                </Field>

                <Field label={t('users.instanceLimit')} hint={t('users.instanceLimitHint')}>
                  {(p) => (
                    <Input
                      {...p}
                      type="number"
                      min={1}
                      max={100}
                      value={limit}
                      onChange={(e) => setLimit(Number(e.target.value))}
                    />
                  )}
                </Field>

                {/* A chip group cannot take an htmlFor, so the group is named for
                    screen readers instead and the visible text is its heading. */}
                <div className="space-y-1">
                  <span className="block text-sm font-medium text-gray-700">{t('users.plan')}</span>
                  <SegmentedControl
                    value={plan}
                    onChange={setPlan}
                    label={t('users.plan')}
                    options={PLANS.map((p) => ({ value: p, label: t(capKey('users.plan', p)) }))}
                  />
                </div>

                <Field label={t('users.language')}>
                  {(p) => (
                    <Select {...p} value={lang} onChange={(e) => setLang(e.target.value)}>
                      {supportedLanguages.map((l) => (
                        <option key={l.code} value={l.code}>{l.label}</option>
                      ))}
                    </Select>
                  )}
                </Field>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <Button variant="secondary" onClick={() => setShowForm(false)}>
                  {t('users.cancel')}
                </Button>
                <Button type="submit" loading={createMutation.isPending}>
                  {createMutation.isPending ? t('users.creating') : t('users.create')}
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* Filter bar — always rendered once the first fetch completes so an
            admin can search/filter even on a large user base */}
        {(total > 0 || filtersActive) && (
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <Input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label={t('users.searchLabel', { defaultValue: 'Search users' })}
                placeholder={t('users.searchPlaceholder', { defaultValue: 'Search by email...' })}
                className="pl-9"
              />
            </div>
            <SegmentedControl
              size="sm"
              value={filterStatus}
              onChange={(v) => list.setFilter('status', v)}
              label={t('users.filterStatusLabel', { defaultValue: 'Filter by status' })}
              options={(['all', 'active', 'pending'] as const).map((s) => ({
                value: s,
                label: s === 'all' ? t('users.filterAll', { defaultValue: 'All' }) : t(capKey('users.status', s)),
              }))}
            />
            <SegmentedControl
              size="sm"
              value={filterPlan}
              onChange={(v) => list.setFilter('plan', v)}
              label={t('users.filterPlanLabel', { defaultValue: 'Filter by plan' })}
              options={(['all', ...PLANS] as const).map((p) => ({
                value: p,
                label: p === 'all' ? t('users.filterAll', { defaultValue: 'All' }) : t(capKey('users.plan', p)),
              }))}
            />
          </div>
        )}

        {deleteError && <Alert tone="danger" className="mb-4">{deleteError}</Alert>}

        {/* Content */}
        {isLoading ? (
          <Card className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center gap-4">
                <Skeleton className="w-8 h-8 rounded-full shrink-0" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-3.5 w-48" />
                  <Skeleton className="h-3 w-32" />
                </div>
                <Skeleton className="h-5 w-16 rounded-full" />
                <Skeleton className="h-5 w-16 rounded-full" />
              </div>
            ))}
          </Card>
        ) : total === 0 && !filtersActive ? (
          <Card padded={false}>
            <EmptyState
              className="py-16"
              icon={
                <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              }
              title={t('users.emptyTitle', { defaultValue: 'No users found' })}
              description={t('users.empty', { defaultValue: 'Get started by inviting a new user to your platform.' })}
              action={<Button onClick={openForm}>{t('users.add')}</Button>}
            />
          </Card>
        ) : total === 0 ? (
          <Card padded={false}>
            <EmptyState
              icon={
                <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              }
              title={t('users.noResults', { defaultValue: 'No users match your filters.' })}
              action={
                <LinkButton onClick={clearFilters}>
                  {t('users.clearFilters', { defaultValue: 'Clear filters' })}
                </LinkButton>
              }
            />
          </Card>
        ) : (
          <Card padded={false} className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-left text-xs text-gray-500 uppercase tracking-wide">
                    <SortableTh label={t('users.colUser')} sortKey="email" current={sortKey} dir={sortDir} onSort={toggleSort} className="pl-5 pr-3 py-3" />
                    <SortableTh label={t('users.colStatus')} sortKey="status" current={sortKey} dir={sortDir} onSort={toggleSort} className="px-3 py-3" />
                    <SortableTh label={t('users.colPlan')} sortKey="plan" current={sortKey} dir={sortDir} onSort={toggleSort} className="px-3 py-3" />
                    <SortableTh label={t('users.colLimit')} sortKey="instance_count" current={sortKey} dir={sortDir} onSort={toggleSort} className="px-3 py-3 text-right" align="right" />
                    <th className="px-3 py-3 hidden md:table-cell font-medium">{t('users.colMail', { defaultValue: 'Mail' })}</th>
                    <th className="px-3 py-3 hidden lg:table-cell font-medium">{t('users.colEnterprise', { defaultValue: 'Enterprise' })}</th>
                    <SortableTh label={t('users.lastLoginShort', { defaultValue: 'Last Login' })} sortKey="last_login" current={sortKey} dir={sortDir} onSort={toggleSort} className="px-3 py-3 hidden md:table-cell" />
                    <SortableTh label={t('users.colCreated')} sortKey="created_at" current={sortKey} dir={sortDir} onSort={toggleSort} className="px-3 py-3 hidden lg:table-cell" />
                    <th className="pl-3 pr-5 py-3 w-10" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {users.map((user) => (
                    <UserRow
                      key={user.id}
                      user={user}
                      expanded={expandedId === user.id}
                      onToggle={() => setExpandedId(expandedId === user.id ? null : user.id)}
                      onDelete={() => handleDelete(user.id)}
                      deleting={deleteMutation.isPending}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              page={currentPage}
              pageSize={serverPageSize}
              total={total}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
            />
          </Card>
        )}

        {/* BTK 5651 monthly hosted-domains export */}
        <div className="mt-6 flex items-start justify-between flex-wrap gap-3">
          <p className="text-xs text-gray-500 max-w-xl">{t('users.btkExportHint')}</p>
          <Button variant="secondary" size="sm" loading={btkDownloading} onClick={handleBtkDownload}>
            {t('users.btkExport')}
          </Button>
        </div>
        {btkError && <Alert tone="danger" className="mt-3">{btkError}</Alert>}
      </div>

      {/* Right column: resource usage panel */}
      <ResourceUsagePanel stats={stats} loading={statsLoading || hostStatsLoading} onRefresh={handleRefreshStats} hostStats={hostStats} />
    </div>
  )
}
